import { useState } from "react";
import { BUILD_TYPE } from "../lib/constants";
import { isIOS } from "../lib/nativeAuth";
import { PUBLIC_WEB_BASE } from "../lib/api";
import { openInAppBrowser } from "../lib/platform";

// Legal links use the public https origin, same as the Account sheet (on iOS the app
// origin is capacitor://localhost, which openInAppBrowser rejects).
const openLegal = (file) => openInAppBrowser(`${PUBLIC_WEB_BASE}/${file}`);
const legalLink = {background:"none",border:"none",padding:0,font:"inherit",color:"#94a3b8",textDecoration:"underline",cursor:"pointer"};

export default function AuthScreen({onAuth, onSignIn, onSignInApple}) {
  const [loading, setLoading] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [error, setError] = useState(null);

  const continueAsGuest = () => onAuth(null);

  const handleSignIn = async () => {
    setLoading(true);
    setError(null);
    const result = await onSignIn();
    if (result && !result.ok) {
      const msg = result.error?.message?.toLowerCase() || '';
      const isCancelled = msg.includes('cancel') || msg.includes('dismiss') || msg.includes('popup_closed');
      setError(isCancelled
        ? "Sign-in was cancelled or did not complete. Tap Continue with Google to try again."
        : "Couldn't complete Google sign-in. Please try again.");
    }
    setLoading(false);
  };

  // Apple sign-in: copy is driven by the returned `cancelled` flag (owned by useAuth) —
  // AuthScreen does not re-parse error.message and emits no telemetry for Apple (#1002).
  const handleSignInApple = async () => {
    setLoadingApple(true);
    setError(null);
    const result = await onSignInApple();
    if (result && !result.ok) {
      setError(result.cancelled
        ? "Sign-in was cancelled or did not complete. Tap Continue with Apple to try again."
        : "Couldn't complete Apple sign-in. Please try again.");
    }
    setLoadingApple(false);
  };

  return (
    <div style={{minHeight:"100vh",minHeight:"100dvh",background:"#050d1a",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px",fontFamily:"'SF Pro Display','DM Sans',system-ui,sans-serif",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
      <div style={{width:"100%",maxWidth:360}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:48}}>
          <div style={{fontSize:13,fontWeight:800,letterSpacing:"4px",color:"#334155",marginBottom:12}}>SLIPTRACK</div>
          <div style={{fontSize:28,fontWeight:800,color:"#f1f5f9",lineHeight:1.2,marginBottom:8}}>Your betting companion</div>
          <div style={{fontSize:14,color:"#475569",lineHeight:1.6}}>Track slips, follow friends, win together</div>
        </div>

        {/* Sign in buttons */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <button onClick={handleSignIn} disabled={loading}
            style={{width:"100%",padding:"14px",background:"#fff",border:"none",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",gap:10,fontSize:15,fontWeight:600,color:"#1a1a1a",cursor:loading?"default":"pointer",fontFamily:"inherit",opacity:loading?0.7:1}}>
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/></svg>
            {loading ? "Signing in..." : "Continue with Google"}
          </button>

          {isIOS() && (
            <button onClick={handleSignInApple} disabled={loadingApple}
              style={{width:"100%",padding:"14px",background:"#000",border:"none",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",gap:10,fontSize:15,fontWeight:600,color:"#fff",cursor:loadingApple?"default":"pointer",fontFamily:"inherit",opacity:loadingApple?0.7:1}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.88 2.65 3.22 2.6 1.29-.05 1.78-.83 3.34-.83 1.56 0 2 .83 3.37.81 1.39-.03 2.27-1.27 3.12-2.53.98-1.45 1.39-2.85 1.41-2.92-.03-.01-2.71-1.04-2.74-4.13zM14.6 4.59c.71-.86 1.19-2.06 1.06-3.25-1.02.04-2.26.68-2.99 1.54-.66.76-1.23 1.98-1.08 3.15 1.14.09 2.3-.58 3.01-1.44z"/></svg>
              {loadingApple ? "Signing in..." : "Continue with Apple"}
            </button>
          )}

          {BUILD_TYPE !== 'Beta' && (
            <button onClick={continueAsGuest} disabled={loading}
              style={{width:"100%",padding:"14px",background:"transparent",border:"1px solid #1e293b",borderRadius:12,fontSize:14,fontWeight:600,color:"#475569",cursor:loading?"default":"pointer",fontFamily:"inherit",opacity:loading?0.6:1}}>
              Continue as Guest
            </button>
          )}
        </div>

        {error && <div style={{marginTop:16,padding:"10px 14px",background:"#1c0101",border:"1px solid #f8717133",borderRadius:10,fontSize:12,color:"#f87171",textAlign:"center",lineHeight:1.5}}>{error}</div>}

        <div style={{marginTop:24,fontSize:11,color:"#64748b",textAlign:"center",lineHeight:1.6}}>
          By continuing you agree to our{" "}
          <button type="button" style={legalLink} onClick={() => openLegal("terms.html")}>Terms of Service</button>
          {" "}and{" "}
          <button type="button" style={legalLink} onClick={() => openLegal("privacy.html")}>Privacy Policy</button>.
        </div>
      </div>
    </div>
  );
}
